<template>
  <div 
    class="sui-input"
    :class="inputClasses"
    tabindex="0"
    @focus="onInputFocus"
  >
    <div class="sui-input__wrapper">
      <input
        ref="input"
        class="sui-input__control"
        :class="{ 'sui-input__control--has-value': modelValue, 'sui-input__control--with_label': label }"
        :value="modelValue"
        :disabled="disabled || readonly"
        :name="name"
        autocomplete="off"
        :placeholder="label ? '' : placeholder"
        :type="type" 
        :maxlength="maxLength"
        @input="onInput"
        @focus="onInputControlFocus"
        @blur="onInputControlBlur"
      />
      <label v-if="label" class="sui-input__label" :for="name">
        <span> {{ label }} </span>
        <span v-if="required" class="required-indicator">*</span>
      </label>
    </div>
    <div class="sui-input__right-slot">
      <slot name="right-slot"></slot>
    </div>
  </div>
</template>

<!-- TODO:  DEBOUNCE -->

<script setup lang="ts">
  import { toRefs, computed, ref, onMounted } from 'vue'

  import Inputmask from 'inputmask'

  const props = defineProps({
    modelValue: {
      type: String,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false
    },
    readonly: {
      type: Boolean,
      default: false,
      required: false
    },
    name: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      dafault: false
    },
    type: {
      type: String,
      default: 'text'
    },
    hasErrors: {
      type: Boolean,
      default: false
    },
    mask: {
      type: Array,
      default: () => []
    },
    unmasked: {
      type: Boolean,
      default: true
    },
    showMask: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    maskMark: {
      type: String,
      default: '0'
    },
    maxLength: {
      type: Number,
      default: undefined
    }
  })

  const { disabled, readonly, modelValue, label, name, required, hasErrors, mask, unmasked, showMask, placeholder, maskMark, maxLength } = toRefs(props)

  const emit = defineEmits(['update:modelValue', 'focus', 'blur'])

  const input = ref<HTMLElement| null>(null)
  const focused = ref<Boolean>(false)
  let input_mask: any = ref(null)

  const inputClasses = computed(() => {
    return {
      'sui-input--disabled': disabled.value,
      'sui-input--readonly': readonly.value,
      'sui-input--has-errors': hasErrors.value,
      'sui-input--focused': focused.value
    }
  })
  
  Inputmask.extendDefinitions({
    '#': {
      validator: "[0-9]"
    },
    '*': {
      validator: "[0-9A-Za-z\u0410-\u044F\u0401\u0451\u00C0-\u00FF\u00B5]"
    }
  })

  function onInput(event: Event): void {
    emit('update:modelValue', (event.target as HTMLInputElement).value)
  }

  function onInputFocus(): void {
    input.value?.focus()
  }

  function onInputControlFocus(event: Event): void {
    focused.value = true
    emit('focus', event)
  }

  function onInputControlBlur(event: Event): void {
    focused.value = false
    emit('blur', event)
  }

  onMounted(() => {
    if(mask.value.length > 0) {
      input_mask.value = new Inputmask({
        mask: mask.value,
        autoUnmask: unmasked.value,
        keepStatic: false,
        escapeChar: '~',
        showMaskOnHover: false,
        showMaskOnFocus: showMask.value && !placeholder.value,
        placeholder: showMask.value && !placeholder.value ? maskMark.value : ' '
      })
      input_mask?.value?.mask?.(input.value)
    }
  })
</script>

<style scoped lang="scss">
.required-indicator {
  color: #FF0A47
}

.sui-input {
  display: flex;
  align-items: center;
  height: 52px;
  width: 100%;
  padding: 7px 12px 6px 12px;
  border-radius: 4px;
  border: 1px var(--stroke-select) solid;
  box-sizing: border-box;

  &--focused, &:hover {
    border: 1px var(--color-icon-primary) solid;
  }

  &--disabled {
    pointer-events: none;
    background-color: var(--color-bg-tertiary);
    color: var(--color-text-secondary);
    border-color: var(--stroke-select);
  }

  &--readonly {
    pointer-events: none;
    background-color: #FFF;
    color: var(--color-text-tertiary);
    border-color: var(--stroke-select);
  }
  
  &--has-errors {
    border-color: var(--color-button-default);
  }

  &__wrapper {
    height: 100%;
    flex-grow: 1;
    display: flex;
    align-items: center;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    position: relative;
  }

  &__right-slot {
    height: 24px;
    width: 24px;
  }

  &__control {
    height: 20px;
    border: 0px transparent;
    width: 100%;
    background-color: transparent;
    color: inherit;
    padding: 0px;
    outline: none;   
    align-self: center;
    cursor: inherit;

    &:focus + label, &--has-value + label {
      top: 0px;
      font-size: 12px;
      line-height: 16px;
      transform: translateY(0);
    }

    &--with_label {
      align-self: flex-end;
    }
  }

  &__label {
    top: 8px;
    pointer-events: none;
    position: absolute;
    margin-bottom: 0;
    transform-origin: left;
    color: #BABCC2;
    transition: all 0.2s ease-out;
  }  
}
</style>
